import jsPDF from "jspdf"
import "jspdf-autotable"
import { deliverPdf } from "./helpers/pdfDelivery.js"
import { drawPdfLogo } from "./helpers/pdfLogo.js"

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0)

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A"
  const dateObj = new Date(dateValue)
  if (Number.isNaN(dateObj.getTime())) return String(dateValue)
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(dateObj)
}

const mapTransaccionesForPdf = (data, idMedioPago) => {
  if (!Array.isArray(data)) return []

  return data.map((tx) => {
    const pagos = (tx.pagos || []).map((p) => ({
      descripcion: p.medioDePago,
      monto: p.montoTotal,
      idMedioPago: p.idMedio,
    }))

    const pagoSeleccionado = pagos.find(
      (pago) => pago.idMedioPago === idMedioPago
    )

    const nombreEntidad = tx.entidad?.descripcion || ""
    const apellidoEntidad = tx.entidad?.apellido || ""
    const entidadCompleta = apellidoEntidad
      ? `${nombreEntidad} ${apellidoEntidad}`.trim()
      : nombreEntidad

    return {
      fecha: formatDate(tx.fechaHoraCreacion),
      montoTotal: tx.montoTotal,
      montoRestante: pagoSeleccionado ? pagoSeleccionado.monto : 0,
      tipoTransaccion: tx.tipoTransaccion?.descripcion || "",
      medioPago:
        pagos.length > 1 ? "Multiple" : pagos[0]?.descripcion || "N/A",
      entidad: entidadCompleta,
      grupo: tx.grupo || "",
      ubicacion: tx.ubicacion?.descripcion || "",
      descripcion: tx.descripcion || "",
      usuario: tx.usuario?.usuario || "",
    }
  })
}

const buildFiltrosTexto = ({
  selectedTipoTransaccion,
  selectedTipoMedioDePago,
  selectedMedioDePago,
  selectedEntidad,
  selectedGrupo,
  selectedUbicacion,
  fechaDesde,
  fechaHasta,
}) => {
  const filtros = []

  filtros.push(`Rango: ${fechaDesde || "N/A"} - ${fechaHasta || "N/A"}`)

  if (selectedTipoTransaccion) {
    filtros.push(`Tipo: ${selectedTipoTransaccion.descripcion}`)
  }
  if (selectedTipoMedioDePago) {
    filtros.push(`Tipo pago: ${selectedTipoMedioDePago.descripcion}`)
  }
  if (selectedMedioDePago) {
    filtros.push(`Medio pago: ${selectedMedioDePago.descripcion}`)
  }
  if (selectedEntidad) {
    const entidad =
      selectedEntidad.label ||
      `${selectedEntidad.descripcion || ""} ${selectedEntidad.apellido || ""}`.trim()
    filtros.push(`Entidad: ${entidad}`)
  }
  if (selectedGrupo) {
    filtros.push(`Grupo: ${selectedGrupo.descripcion || selectedGrupo.label || ""}`)
  }
  if (selectedUbicacion) {
    filtros.push(`Ubicación: ${selectedUbicacion.descripcion || selectedUbicacion.label || ""}`)
  }

  return filtros
}

const getColumnStyles = (incluirMontoSeleccionado) => {
  if (incluirMontoSeleccionado) {
    return {
      0: { cellWidth: 85, halign: "center" },
      1: { cellWidth: 70, halign: "right" },
      2: { cellWidth: 70, halign: "right" },
      3: { cellWidth: 75, overflow: "linebreak" },
      4: { cellWidth: 85, overflow: "linebreak" },
      5: { cellWidth: 90, overflow: "linebreak" },
      6: { cellWidth: 70, overflow: "linebreak" },
      7: { cellWidth: 85, overflow: "linebreak" },
      8: { cellWidth: 95, overflow: "linebreak" },
      9: { cellWidth: 70, overflow: "linebreak" },
    }
  }

  return {
    0: { cellWidth: 90, halign: "center" },
    1: { cellWidth: 75, halign: "right" },
    2: { cellWidth: 85, overflow: "linebreak" },
    3: { cellWidth: 85, overflow: "linebreak" },
    4: { cellWidth: 95, overflow: "linebreak" },
    5: { cellWidth: 80, overflow: "linebreak" },
    6: { cellWidth: 80, overflow: "linebreak" },
    7: { cellWidth: 90, overflow: "linebreak" },
    8: { cellWidth: 95, overflow: "linebreak" },
    9: { cellWidth: 80, overflow: "linebreak" },
  }
}

export const generarPdfTablaTransacciones = ({
  transacciones = [],
  medioDePagoId = null,
  selectedMedioDePago = null,
  selectedTipoTransaccion = null,
  selectedTipoMedioDePago = null,
  selectedEntidad = null,
  selectedGrupo = null,
  selectedUbicacion = null,
  fechaDesde = "",
  fechaHasta = "",
  usuario = "Usuario",
  logoBase64 = null,
  deliveryContext = null,
}) => {
  const rowsForPdf = mapTransaccionesForPdf(transacciones, medioDePagoId)
  if (!rowsForPdf.length) return false

  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20

  const PAL = {
    primary: [25, 118, 210],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
  }

  drawPdfLogo({ doc, logoBase64, x: margin, y: margin, maxW: 80, maxH: 36 })

  let yPosition = 36

  doc.setFont("helvetica", "bold")
  doc.setFontSize(16)
  doc.setTextColor(...PAL.primary)
  doc.text("REPORTE DE TRANSACCIONES", pageWidth / 2, yPosition, {
    align: "center",
  })
  yPosition += 20

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.inkLight)
  doc.text(
    `Fecha de generacion: ${formatDate(new Date())} | Usuario: ${usuario || "Usuario"}`,
    pageWidth - margin,
    yPosition,
    { align: "right" }
  )
  yPosition += 24

  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...PAL.ink)
  doc.text("Filtros aplicados:", margin, yPosition)
  yPosition += 14

  doc.setDrawColor(...PAL.primary)
  doc.setLineWidth(1)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 12

  const filtrosTexto = buildFiltrosTexto({
    selectedTipoTransaccion,
    selectedTipoMedioDePago,
    selectedMedioDePago,
    selectedEntidad,
    selectedGrupo,
    selectedUbicacion,
    fechaDesde,
    fechaHasta,
  })

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.muted)
  doc.text(filtrosTexto.join(" | "), margin, yPosition, {
    maxWidth: pageWidth - margin * 2,
  })
  yPosition += 24

  doc.setFillColor(...PAL.bgSoft)
  doc.roundedRect(margin, yPosition - 13, 240, 20, 3, 3, "F")
  doc.setFont("helvetica", "bold")
  doc.setFontSize(10)
  doc.setTextColor(...PAL.ink)
  doc.text(`TOTAL DE REGISTROS: ${rowsForPdf.length}`, margin + 8, yPosition)
  yPosition += 24

  const incluirMontoSeleccionado = Boolean(selectedMedioDePago)
  const head = [
    "Fecha",
    "Monto",
    ...(incluirMontoSeleccionado ? ["Monto pago seleccionado"] : []),
    "Tipo transaccion",
    "Medio de pago",
    "Entidad",
    "Grupo",
    "Ubicacion",
    "Descripcion",
    "Usuario",
  ]

  const body = rowsForPdf.map((row) => [
    row.fecha,
    formatCurrency(row.montoTotal),
    ...(incluirMontoSeleccionado ? [formatCurrency(row.montoRestante)] : []),
    row.tipoTransaccion,
    row.medioPago,
    row.entidad,
    row.grupo,
    row.ubicacion,
    row.descripcion,
    row.usuario,
  ])

  doc.autoTable({
    startY: yPosition,
    head: [head],
    body,
    theme: "grid",
    headStyles: {
      fillColor: PAL.primary,
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: PAL.ink,
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    columnStyles: getColumnStyles(incluirMontoSeleccionado),
    margin: { left: margin, right: margin },
    didDrawPage: (data) => {
      doc.setFont("helvetica", "normal")
      doc.setFontSize(8)
      doc.setTextColor(...PAL.inkLight)
      const pageText = `Pagina ${data.pageNumber} de ${doc.internal.pages.length - 1}`
      doc.text(pageText, pageWidth / 2, pageHeight - 20, { align: "center" })
    },
  })

  const fileName = `transacciones_${new Date().toISOString().slice(0, 10)}.pdf`
  doc.setProperties({ title: fileName, subject: "Reporte de Transacciones", creator: "Sistema" })
  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
  return true
}
